import React, { useState, useEffect, useMemo } from "react";
import ReviewChart from "./ReviewChart";
import "../../../../dist/assets/stylesheets/css/reviewPatient.css";
import "../../../../dist/assets/stylesheets/css/legacyAnalytics.css";
import { getMetrics, getValuesMetrics, saveSensorOffset, getFloorDetect } from "../../../util/metricsAPI";
import { getTrialInfo } from "../../../util/trialApi";
import DisplayCharts from "./DisplayCharts";
import SensorOffsetSlider from "./SensorOffsetSlider";
import ContactMarkerToggles from "./ContactMarkerToggles";
import { useSelector, useDispatch } from 'react-redux';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { chartActions } from "../../../store/chart-slice";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import {
  discoverDefaultSensorMetrics,
  getOffsetForSensorMetric,
  listSensorDeviceIds,
  resolveSensorMetricEntries,
  sensorDeviceLabel,
} from "../../../util/sensorMetrics";
import {
  CONTACT_ANGLE_COLUMNS,
  FLOOR_HEIGHT_DT_COLUMNS,
  buildContactEventsFromAngleSeries,
  hasFloorDetect,
  isFloorHeightMetric,
  isPlottableAngleMetric,
  resolveContactSampleRateHz,
} from "../../../util/floorContacts";
import { mergeAnnotationEvents, splitContactEventsBySavedFrames } from "../../../util/contactAnnotations";
import { LEGACY_ANGLE_METRICS, LEGACY_FPS } from "../../../util/legacyAnalytics";
import {
  defaultLegacySensorNames,
  groupLegacySensors,
  indexLegacySensors,
  legacySensorDeviceLabel,
} from "../../../util/legacySensors";

const CHART_HEIGHT_BY_VIDEO_LAYOUT = {
  compact: 200,
  wide: 240,
  fullscreen: 220,
};

function ReviewChartsContainer(props) {
  const { trial, currentTime, videoDuration, videoLayoutMode = "compact" } = props;
  const chartHeight =
    CHART_HEIGHT_BY_VIDEO_LAYOUT[videoLayoutMode] ||
    CHART_HEIGHT_BY_VIDEO_LAYOUT.compact;
  const list_metrics = useSelector(state => state.chart.list_metrics);
  const list_sensor_metrics = useSelector(state => state.chart.list_sensor_metrics);
  const dispatch = useDispatch();
  const [reviewCharts, setReviewCharts] = useState([]);
  const [reviewChartName, setReviewChartName] = useState("");
  const [combinedItems, setCombinedItems] = useState([]);

  const [sensorOffsets, setSensorOffsets] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [floorContacts, setFloorContacts] = useState([]);
  const [contactsAvailable, setContactsAvailable] = useState(false);
  const [floorByTrial, setFloorByTrial] = useState({ trialId: null, floor: null });
  const activeFloor = floorByTrial.trialId === trial ? floorByTrial.floor : null;
  const userAnnotations = props.annotations;
  const shownContacts = useMemo(
    () => mergeAnnotationEvents(floorContacts, userAnnotations),
    [floorContacts, userAnnotations]
  );
  const showContactMarkers = contactsAvailable || (
    Array.isArray(userAnnotations) && userAnnotations.length > 0
  );

  const legacy = !!props.legacy;
  const evowalkEnabled = !legacy && localStorage.getItem('evowalkIntegrationEnabled') === 'true';

  useEffect(() => {
    if (!trial || trial === '0') {
      setFloorByTrial({ trialId: trial || null, floor: null });
      setFloorContacts([]);
      setContactsAvailable(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Legacy trials have no floor detection, but clinician marks still live
        // in the contact columns on the fixed 60 Hz clock, so reload those.
        let contactClock = { fps: LEGACY_FPS };
        if (!legacy) {
          // Availability = floor_detect present (dedicated endpoint, else trial.floor_detect).
          // Any floor object shows the charts — do not require stance_heel_ransac.
          // Event samples are separate; toggles still show when floor exists but
          // contact columns are empty / not yet populated.
          let floorRes = await getFloorDetect(trial);
          if (cancelled) return;
          if (!hasFloorDetect(floorRes)) {
            const trialRes = await getTrialInfo(trial);
            if (cancelled) return;
            const embedded = trialRes?.trial?.floor_detect;
            if (embedded && typeof embedded === 'object' && !Array.isArray(embedded)) {
              floorRes = { status: 'success', floor_detect: embedded };
            }
          }
          if (!hasFloorDetect(floorRes)) {
            setFloorByTrial({ trialId: trial, floor: null });
            setFloorContacts([]);
            setContactsAvailable(false);
            return;
          }
          const floor = floorRes.floor_detect;
          contactClock = floor;
          setFloorByTrial({ trialId: trial, floor });
          setContactsAvailable(true);
        } else {
          setFloorByTrial({ trialId: trial, floor: null });
        }
        let annotationFrames = null;
        try {
          const trialRes = await getTrialInfo(trial);
          if (cancelled) return;
          annotationFrames = trialRes?.trial?.contact_annotation_frames || null;
        } catch (err) {
          console.log(err);
        }
        const seriesList = await Promise.all(
          CONTACT_ANGLE_COLUMNS.map(async (name) => {
            try {
              const series = await getValuesMetrics(trial, 'angles', name);
              return { name, series: series || null };
            } catch {
              return { name, series: null };
            }
          })
        );
        if (cancelled) return;
        const events = buildContactEventsFromAngleSeries(seriesList, contactClock);
        const split = splitContactEventsBySavedFrames(
          events,
          annotationFrames,
          resolveContactSampleRateHz(null, contactClock)
        );
        setFloorContacts(split.pipeline);
        if (legacy) setContactsAvailable(split.pipeline.length > 0);
        if (typeof props.onSavedAnnotations === 'function') {
          props.onSavedAnnotations(trial, split.annotations);
        }
      } catch (err) {
        console.log(err);
        if (!cancelled) {
          setFloorByTrial({ trialId: trial, floor: null });
          setFloorContacts([]);
          setContactsAvailable(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [trial, legacy]);

  useEffect(() => {
    // Device sync is already in the served series. Do not load stored
    // offset_sec into the sliders — that would shift the graph again.
    setSensorOffsets({});
  }, [trial]);

  // Legacy trials list their EvoWalk channels in the catalog; the series are
  // already on the 60 Hz angle grid, so no availability probe is needed.
  const [legacySensors, setLegacySensors] = useState({ trial: null, groups: [] });
  useEffect(() => {
    if (!legacy) return undefined;
    let cancelled = false;
    setLegacySensors({ trial, groups: [] });
    dispatch(chartActions.updateListSensorMetrics([]));
    if (!trial || trial === '0') return undefined;
    getMetrics(trial).then((dict) => {
      if (cancelled) return;
      const groups = groupLegacySensors(dict && dict.sensors);
      setLegacySensors({ trial, groups });
      dispatch(chartActions.updateListSensorMetrics(defaultLegacySensorNames(groups)));
    });
    return () => {
      cancelled = true;
    };
  }, [trial, legacy]);
  const legacySensorIndex = useMemo(
    () => indexLegacySensors(legacySensors.groups),
    [legacySensors]
  );

  const sensorDeviceIds = legacy
    ? legacySensors.groups
      .map((group) => group.deviceId)
      .filter((id) => list_sensor_metrics.some((name) => legacySensorIndex.get(name)?.deviceId === id))
    : listSensorDeviceIds(list_sensor_metrics);
  const sensorOffsetFor = (name) => (
    legacy
      ? sensorOffsets[legacySensorIndex.get(name)?.deviceId] ?? 0
      : getOffsetForSensorMetric(name, sensorOffsets)
  );

  const handleSaveOffset = async () => {
    setIsSaving(true);
    setSaveStatus(null);
    try {
      const offsetsByDevice = {};
      sensorDeviceIds.forEach((id) => {
        offsetsByDevice[id] = sensorOffsets[id] ?? 0;
      });
      await saveSensorOffset(trial, { offsetsByDevice });
      setSaveStatus('saved');
    } catch {
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  useEffect(() => {
    const allAngleMetrics = [
      "knee_right_flex", "knee_left_flex", "knee_right_rot", "knee_left_rot",
      "hip_right_flex", "hip_left_flex", "hip_right_rot", "hip_left_rot", "hip_right_abduct", "hip_left_abduct",
      "pelvic_tilt_left", "pelvic_tilt_right", "pelvic_rot_left", "pelvic_rot_right", "pelvic_obl_left", "pelvic_obl_right",
      "elbow_right_flex", "elbow_left_flex",
      "shoulder_right_flex", "shoulder_left_flex", "shoulder_right_rot", "shoulder_left_rot", "shoulder_right_abduct", "shoulder_left_abduct",
      "neck_flex", "neck_rot",
    ];

    const jointMetrics = legacy
      ? LEGACY_ANGLE_METRICS
      : evowalkEnabled
        ? ["hip_right_flex", "hip_left_flex", "knee_right_flex", "knee_left_flex"]
        : allAngleMetrics;
    const heightMetrics = activeFloor
      ? FLOOR_HEIGHT_DT_COLUMNS.filter(isPlottableAngleMetric)
      : [];

    let cancelled = false;
    const validMetrics = [];

    (async () => {
      const heightSettled = await Promise.all(heightMetrics.map(async (metric) => {
        const res = await getValuesMetrics(trial, "angles", metric);
        if (cancelled) return null;
        console.log('fetch metrics', res);
        const valid = !!(res && res.values && res.values.some(value => value[0] !== null));
        return valid ? metric : null;
      }));
      if (cancelled) return;
      heightSettled.forEach((metric) => {
        if (metric) validMetrics.push(metric);
      });
      // Show detrended height as soon as floor_detect is known. Joint probes
      // stay sequential and replace this list when they finish.
      if (validMetrics.length) {
        const alreadyShown = list_metrics.filter(
          (name) => isPlottableAngleMetric(name) && !validMetrics.includes(name)
        );
        dispatch(chartActions.updateListMetrics([...validMetrics, ...alreadyShown]));
      }

      for (let metric of jointMetrics) {
        if (cancelled || !isPlottableAngleMetric(metric)) continue;
        const metricType = "angles";
        await getValuesMetrics(trial, metricType, metric).then((res) => {
          if (cancelled) return;
          console.log('fetch metrics', res);
          const valid = !!(res && res.values && res.values.some(value => value[0] !== null));
          if (valid) {
            validMetrics.push(metric);
          }
        });
      }
      if (cancelled) return;

      dispatch(chartActions.updateListMetrics(validMetrics));

      if (evowalkEnabled) {
        const trialRes = await getTrialInfo(trial);
        const trialName = trialRes?.trial?.name || trialRes?.trial?.date || null;
        const dict = await getMetrics(trial, trialName);
        const sensorEntries = resolveSensorMetricEntries(dict || {});
        const sensorNames = sensorEntries.map((entry) => entry.apiName);

        const defaultNames = discoverDefaultSensorMetrics(sensorNames);
        const validSensors = [];
        for (let name of defaultNames) {
          if (cancelled) return;
          const res = await getValuesMetrics(trial, 'sensors', name, trialName);
          if (cancelled) return;
          if (res && res.values && res.values.length > 0) {
            validSensors.push(name);
          }
        }
        if (cancelled) return;
        dispatch(chartActions.updateListSensorMetrics(validSensors));
      } else if (!legacy) {
        dispatch(chartActions.updateListSensorMetrics([]));
      }

      if (cancelled) return;
      setReviewCharts([
        <ReviewChart
          handleMetricNameChange={setReviewChartName}
          trial={trial}
          key={new Date().getTime()}
        />,
      ]);
    })();
    return () => {
      cancelled = true;
    };
  }, [trial, activeFloor, legacy]);

  const deleteChart = (index) => {
    let copy = [...list_metrics];
    copy.splice(index, 1);
    console.log("delete called", list_metrics, copy);
    dispatch(chartActions.updateListMetrics(copy));
  };

  const deleteSensorChart = (index) => {
    let copy = [...list_sensor_metrics];
    copy.splice(index, 1);
    dispatch(chartActions.updateListSensorMetrics(copy));
  };

  useEffect(() => {
    const incoming = [
      ...list_metrics
        .filter(isPlottableAngleMetric)
        .map((m) => ({ id: `angle-${m}`, type: 'angles', metric: m })),
      ...(evowalkEnabled || legacy
        ? list_sensor_metrics.map((m) => ({ id: `sensor-${m}`, type: 'sensors', metric: m }))
        : []),
    ];
    const incomingIds = new Set(incoming.map((i) => i.id));
    setCombinedItems((prev) => {
      const kept = prev.filter((i) => incomingIds.has(i.id));
      const keptIds = new Set(kept.map((i) => i.id));
      const added = incoming.filter((i) => !keptIds.has(i.id));
      return [...kept, ...added];
    });
  }, [list_metrics, list_sensor_metrics, legacy]);

  const handleOnDragEnd = (result) => {
    if (!result.destination) return;
    const items = [...combinedItems];
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setCombinedItems(items);
    dispatch(chartActions.updateBothLists({
      list_metrics: items.filter((i) => i.type === 'angles').map((i) => i.metric),
      list_sensor_metrics: items.filter((i) => i.type === 'sensors').map((i) => i.metric),
    }));
  };

  const deleteByItem = (item) => {
    if (item.type === 'angles') {
      const idx = list_metrics.indexOf(item.metric);
      deleteChart(idx);
    } else {
      const idx = list_sensor_metrics.indexOf(item.metric);
      deleteSensorChart(idx);
    }
  };

  const saveLabel = isSaving
    ? 'Saving…'
    : saveStatus === 'saved'
      ? '✓ Saved'
      : saveStatus === 'error'
        ? 'Error'
        : 'Save Sync';

  const saveBtnColor =
    saveStatus === 'saved' ? '#4caf50'
      : saveStatus === 'error' ? '#f44336'
        : '#63C7CF';

  const legacySensorChartProps = (name) => {
    const entry = legacySensorIndex.get(name);
    if (!entry) return {};
    const label = legacySensors.groups.length > 1
      ? `${entry.channel} · ${entry.deviceLabel}`
      : entry.channel;
    return { label, stepped: entry.stepped };
  };

  const sensorOffsetPanel = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        px: 2,
        py: 1.5,
        mb: 2,
        bgcolor: '#f8f8f8',
        borderRadius: 2,
        border: '1px solid #e0e0e0',
      }}
    >
      {legacy ? (
        <p className="legacy-sensors__note">
          EvoWalk values are raw, as each device revision logged them, without units. Timing comes
          from the legacy lab's merge and is unverified below about 100 ms; nudge a device here.
        </p>
      ) : null}
      {sensorDeviceIds.map((id) => {
        const deviceLabel = legacy ? legacySensorDeviceLabel(id) : sensorDeviceLabel(id);
        const label = deviceLabel
          ? `Sensor Time Offset (${deviceLabel})`
          : 'Sensor Time Offset';
        return (
          <SensorOffsetSlider
            key={id}
            label={label}
            offsetSec={sensorOffsets[id] ?? 0}
            onOffsetChange={(value) => setSensorOffsets((prev) => ({ ...prev, [id]: value }))}
            onReset={() => setSensorOffsets((prev) => ({ ...prev, [id]: 0 }))}
          />
        );
      })}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip
          title="Saves a manual residual only. Device sync is already in the series, so these sliders load at 0 and are not applied again."
          placement="top"
        >
          <Button
            size="small"
            variant="contained"
            onClick={handleSaveOffset}
            disabled={isSaving}
            sx={{
              minWidth: 100,
              textTransform: 'none',
              bgcolor: saveBtnColor,
              '&:hover': { bgcolor: saveBtnColor, filter: 'brightness(0.9)' },
              transition: 'background-color 0.3s',
            }}
          >
            {saveLabel}
          </Button>
        </Tooltip>
      </Box>
    </Box>
  );

  return trial !== "0" ? (
    <section className="container">

      {showContactMarkers ? <ContactMarkerToggles /> : null}

      {(evowalkEnabled || legacy) && sensorDeviceIds.length > 0 && sensorOffsetPanel}

      <DragDropContext onDragEnd={handleOnDragEnd}>
        <Droppable droppableId="droppable-all">
          {(provided) => (
            <ul
              className="review-charts-container"
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {combinedItems.map((item, index) => (
                <Draggable key={item.id} index={index} draggableId={item.id}>
                  {(provided) => (
                    <li key={item.id} {...provided.draggableProps} ref={provided.innerRef}>
                      <DisplayCharts
                        inComparePage={props.inComparePage}
                        providedDragHandleProps={{ ...provided.dragHandleProps }}
                        videoRef={props.videoRef}
                        deleteChart={() => deleteByItem(item)}
                        setCurrentTime={props.setCurrentTime}
                        currentTime={currentTime}
                        videoDuration={props.videoDuration}
                        key={item.id}
                        trial={trial}
                        metricName={item.metric}
                        metricType={item.type}
                        {...(legacy && item.type === 'sensors' ? legacySensorChartProps(item.metric) : {})}
                        axisYTitle={
                          item.type === 'sensors'
                            ? 'Value'
                            : isFloorHeightMetric(item.metric)
                              ? 'm'
                              : 'Degrees'
                        }
                        sampleRateHz={
                          legacy
                            ? LEGACY_FPS
                            : isFloorHeightMetric(item.metric)
                              ? resolveContactSampleRateHz(null, activeFloor)
                              : undefined
                        }
                        sensorOffset={
                          item.type === 'sensors'
                            ? sensorOffsetFor(item.metric)
                            : 0
                        }
                        chartHeight={chartHeight}
                        floorContacts={shownContacts}
                        contactsAvailable={showContactMarkers}
                        annotateMode={props.annotateMode}
                        annotateType={props.annotateType}
                        onPlaceAnnotation={props.onPlaceAnnotation}
                        onRemoveAnnotation={props.onRemoveAnnotation}
                      />
                    </li>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>
    </section>
  ) : null;
}

export default ReviewChartsContainer;
